import React, { useEffect, useState } from 'react';
import { Plus, Trash2, Save } from 'lucide-react';
import api from '@/lib/axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

export default function GkzGroupsTab() {
  const [groups, setGroups] = useState([]);
  const [codes, setCodes] = useState([]);
  const [name, setName] = useState('');
  const [selectedCodes, setSelectedCodes] = useState([]);
  const [editingId, setEditingId] = useState(null);

  const load = () => {
    api.get('/gkz-groups').then((r) => setGroups(r.data || [])).catch(() => setGroups([]));
    api.get('/gkz-groups/codes').then((r) => setCodes(r.data || [])).catch(() => setCodes([]));
  };

  useEffect(() => { load(); }, []);

  const toggleCode = (code) => {
    setSelectedCodes((cur) => (
      cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code]
    ));
  };

  const resetForm = () => {
    setName('');
    setSelectedCodes([]);
    setEditingId(null);
  };

  const save = async () => {
    if (!name.trim() || selectedCodes.length === 0) {
      toast.error('Name und mindestens eine GKZ angeben');
      return;
    }
    try {
      const payload = { name: name.trim(), codes: selectedCodes };
      if (editingId) await api.put(`/gkz-groups/${editingId}`, payload);
      else await api.post('/gkz-groups', payload);
      toast.success(editingId ? 'Gruppe gespeichert' : 'Gruppe angelegt');
      resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Speichern fehlgeschlagen');
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gkz-groups/${id}`);
      toast.success('Gruppe gelöscht');
      if (editingId === id) resetForm();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-testid="gkz-groups-tab">
      <div className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
        <h3 className="font-semibold text-slate-800">{editingId ? 'Gruppe bearbeiten' : 'Neue GKZ-Gruppe'}</h3>
        <p className="text-sm text-slate-500">
          Mehrere Gemeindekennzeichen (z. B. Schüttorf und zugehörige Orte) zu einer Abrechnungseinheit zusammenfassen.
        </p>
        <Input
          placeholder="z. B. Samtgemeinde Schüttorf"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="gkz-group-name"
        />
        <div className="flex flex-wrap gap-2">
          {codes.length === 0 ? (
            <p className="text-sm text-slate-400">Keine GKZ bei Zahlungspflichtigen gefunden</p>
          ) : codes.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => toggleCode(c)}
              data-testid={`gkz-code-toggle-${c}`}
              className={`text-xs px-2 py-1 rounded border ${
                selectedCodes.includes(c)
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} data-testid="gkz-group-save">
            {editingId ? <Save className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
            {editingId ? 'Speichern' : 'Anlegen'}
          </Button>
          {editingId && (
            <Button size="sm" variant="outline" onClick={resetForm}>Abbrechen</Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 divide-y">
        {groups.length === 0 ? (
          <p className="p-4 text-sm text-slate-500">Noch keine Gruppen</p>
        ) : groups.map((g) => (
          <div key={g.id} className="p-4 flex items-start justify-between gap-3" data-testid={`gkz-group-row-${g.id}`}>
            <button
              type="button"
              className="text-left min-w-0"
              onClick={() => { setEditingId(g.id); setName(g.name); setSelectedCodes(g.codes || []); }}
            >
              <p className="font-medium text-slate-800">{g.name}</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {(g.codes || []).map((c) => (
                  <Badge key={c} variant="outline" className="text-[10px]">{c}</Badge>
                ))}
              </div>
            </button>
            <Button variant="ghost" size="sm" onClick={() => remove(g.id)} data-testid={`gkz-group-delete-${g.id}`}>
              <Trash2 className="w-4 h-4 text-slate-400" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
